function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, memo, useState } from "react";
import { Droppable } from "react-beautiful-dnd";
import { ItemType, useTopicPage_LoadMoreSectionsLazyQuery, useTopicPage_LoadMorePostsLazyQuery, TopicPage_ViewsTopicFragmentDoc } from "sprout-graphql";
import { EmptyList } from "@sprout/client-components/dist/EmptyList";
import { useApolloClient } from "@apollo/client";
import update from "immutability-helper";
import DragDropContextCustomSensors from "@sprout/client-components/dist/DragDropContextCustomSensors";
import { useIsMyTasksPageOnboard, useIsReadLaterPageOnboard, useIsTopicPageOnboard } from "@sprout/hooks/dist/onboard";
import useSortTopicList from "../../graphql/mutations/useSortTopicList";
import { useTopicPage } from "../../TopicPageContext";
import { useTopicPageViewsTopicEditor } from "../TopicPageViewsTopicEditorContext";
import TopicPageViewsTopicEditors from "../TopicPageViewsTopicEditors";
import TopicPageViewMore from "../../TopicPageViewMore";
import TopicPageSectionNewEditor from "../../TopicPage_Section/TopicPageSectionNewEditor";
import TopicPageViewsTopicListPosts from "./TopicPageViewsTopicListPosts";
import TopicPageViewsTopicListSections from "./TopicPage_Views_Topic_List_Sections";
const TopicPageViewsTopicListMemo = (props)=>{
    const { topic  } = props;
    const { id: topicId , nodeId: topicNodeId , rootTopicOrSelf  } = topic;
    const { owner , special , settings  } = rootTopicOrSelf || {};
    var ref2;
    const __hideLater = (ref2 = settings === null || settings === void 0 ? void 0 : settings.__hideLater) !== null && ref2 !== void 0 ? ref2 : false;
    const posts = topic.posts.nodes;
    const hiddenPostsCount = topic.posts.totalCount - posts.length;
    const hasMorePosts = hiddenPostsCount > 0;
    const postsAfterCursor = topic.posts.pageInfo.endCursor;
    const client = useApolloClient();
    const [fetchMorePosts, { loading: postsLoading  }] = useTopicPage_LoadMorePostsLazyQuery({
        fetchPolicy: "network-only",
        onCompleted: useCallback((data)=>{
            var ref, ref1;
            const options = {
                fragment: TopicPage_ViewsTopicFragmentDoc,
                fragmentName: "TopicPage_ViewsTopic",
                id: topicNodeId
            };
            const cache = client.readFragment(options);
            const fetchMoreResultsNodes = (ref = data.topic) === null || ref === void 0 ? void 0 : ref.posts.nodes;
            const fetchMoreResultsPageInfo = (ref1 = data.topic) === null || ref1 === void 0 ? void 0 : ref1.posts.pageInfo;
            if (!fetchMoreResultsNodes || !fetchMoreResultsPageInfo) return;
            client.writeFragment(_objectSpread({}, options, {
                data: update(cache, {
                    posts: {
                        pageInfo: {
                            $set: fetchMoreResultsPageInfo
                        },
                        nodes: {
                            $apply: (nodes)=>{
                                const knownIds = nodes.map((node)=>node.id);
                                return [
                                    ...nodes,
                                    ...fetchMoreResultsNodes.filter((node)=>!knownIds.includes(node.id)), 
                                ];
                            }
                        }
                    }
                })
            }));
        }, [
            client,
            topicNodeId
        ])
    });
    const loadMorePosts = useCallback(()=>{
        if (!postsAfterCursor) return;
        fetchMorePosts({
            variables: {
                topicId,
                after: postsAfterCursor
            }
        });
    }, [
        fetchMorePosts,
        postsAfterCursor,
        topicId
    ]);
    //
    const sections = topic.sections.nodes;
    const hiddenSectionsCount = topic.sections.totalCount - sections.length;
    const hasMoreSections = hiddenSectionsCount > 0;
    const sectionsAfterCursor = topic.sections.pageInfo.endCursor;
    const [fetchMoreSections, { loading: sectionsLoading  }] = useTopicPage_LoadMoreSectionsLazyQuery({
        fetchPolicy: "network-only",
        onCompleted: useCallback((data)=>{
            var ref, ref3;
            const options = {
                fragment: TopicPage_ViewsTopicFragmentDoc,
                fragmentName: "TopicPage_ViewsTopic",
                id: topicNodeId
            };
            const cache = client.readFragment(options);
            const fetchMoreResultsNodes = (ref = data.topic) === null || ref === void 0 ? void 0 : ref.sections.nodes;
            const fetchMoreResultsPageInfo = (ref3 = data.topic) === null || ref3 === void 0 ? void 0 : ref3.sections.pageInfo;
            if (!fetchMoreResultsNodes || !fetchMoreResultsPageInfo) return;
            client.writeFragment(_objectSpread({}, options, {
                data: update(cache, {
                    sections: {
                        pageInfo: {
                            $set: fetchMoreResultsPageInfo
                        },
                        nodes: {
                            $apply: (nodes)=>{
                                const knownIds = nodes.map((node)=>node.id);
                                return [
                                    ...nodes,
                                    ...fetchMoreResultsNodes.filter((node)=>!knownIds.includes(node.id)), 
                                ];
                            }
                        }
                    }
                })
            }));
        }, [
            client,
            topicNodeId
        ])
    });
    const loadMoreSections = useCallback(()=>{
        if (!sectionsAfterCursor) return;
        fetchMoreSections({
            variables: {
                topicId,
                after: sectionsAfterCursor
            }
        });
    }, [
        fetchMoreSections,
        sectionsAfterCursor,
        topicId
    ]);
    const { disabled , isOwnerOrAdmin  } = useTopicPage();
    const { state  } = useTopicPageViewsTopicEditor();
    const isSortEnabled = state.editor ? false : isOwnerOrAdmin && !disabled;
    // TODO: ?
    // Don't let us get locked into editor being open but somehow unfocussed
    // useActionRegistryGlobalHandler(
    //   "ESCAPE",
    //   useCallback(() => {
    //     if (state.editor !== TopicPageViewsTopicEditorType.NULL) {
    //       dispatch({ type: ActionType.CLOSE_EDITOR });
    //       return true;
    //     }
    //     return false;
    //   }, [state, dispatch])
    // );
    const [expandable, setExpandable] = useState(null);
    const handleBeforeCapture = useCallback((before)=>{
        setExpandable(before.draggableId);
    }, []);
    const sectionsRenderFn = useCallback((dropProvided)=>{
        return(// eslint-disable-next-line @typescript-eslint/unbound-method
        /*#__PURE__*/ _jsxs("div", {
            ref: dropProvided.innerRef,
            children: [
                /*#__PURE__*/ _jsx(TopicPageViewsTopicListSections, {
                    __hideLater: __hideLater,
                    topicId: topicId,
                    sections: sections,
                    owner: owner || undefined,
                    isSortEnabled: isSortEnabled,
                    expandable: expandable
                }),
                dropProvided.placeholder
            ]
        }));
    }, [
        __hideLater,
        topicId,
        sections,
        owner,
        isSortEnabled,
        expandable
    ]);
    const hasSections = sections.length > 0;
    const hasPosts = posts.length > 0;
    const sortTopicList = useSortTopicList({
        posts,
        sections
    });
    const handleDragEnd = useCallback((...args)=>{
        sortTopicList(...args);
        setExpandable(null);
    }, [
        sortTopicList
    ]);
    const isEmpty = posts.length === 0 && sections.length === 0;
    const [isTopicPageOnboard] = useIsTopicPageOnboard();
    const [isMyTasksPageOnboard] = useIsMyTasksPageOnboard();
    const [isReadLaterPageOnboard] = useIsReadLaterPageOnboard();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isEmpty && state.editor === undefined && (!special && isTopicPageOnboard || special === "MY_TASKS" && isMyTasksPageOnboard || special === "INBOX" && isReadLaterPageOnboard) && /*#__PURE__*/ _jsx(EmptyList, {
                text: "No leaves here yet"
            }),
            /*#__PURE__*/ _jsxs(DragDropContextCustomSensors, {
                onDragEnd: handleDragEnd,
                onBeforeCapture: handleBeforeCapture,
                children: [
                    /*#__PURE__*/ _jsx(TopicPageViewsTopicListPosts, {
                        topicId: topicId,
                        topicNodeId: topicNodeId,
                        posts: posts,
                        context: "page",
                        hasMorePosts: hasMorePosts,
                        isSortEnabled: isSortEnabled,
                        mb: hasSections || state.editor === "CREATE_SECTION" ? hasMorePosts ? undefined : hasPosts ? 3 : 1 : undefined,
                        prepend: /*#__PURE__*/ _jsx(TopicPageViewsTopicEditors, {
                            topicId: topicId,
                            topicNodeId: topicNodeId,
                            afterItemId: null,
                            context: "page"
                        }),
                        __hideLater: __hideLater
                    }),
                    hasMorePosts && /*#__PURE__*/ _jsx(TopicPageViewMore, {
                        count: hiddenPostsCount,
                        loadMore: loadMorePosts,
                        loading: postsLoading
                    }),
                    /*#__PURE__*/ _jsx(TopicPageSectionNewEditor, {
                        topic: topic
                    }),
                    sections.length !== 0 && /*#__PURE__*/ _jsx(Droppable, {
                        type: ItemType.Topic,
                        droppableId: JSON.stringify({
                            id: topicId,
                            nodeId: topicNodeId,
                            type: ItemType.Topic
                        }),
                        isCombineEnabled: true,
                        isDropDisabled: !isSortEnabled,
                        children: sectionsRenderFn
                    }),
                    hasMoreSections && /*#__PURE__*/ _jsx(TopicPageViewMore, {
                        count: hiddenSectionsCount,
                        loadMore: loadMoreSections,
                        loading: sectionsLoading
                    })
                ]
            })
        ]
    });
};
export default /*#__PURE__*/ memo(TopicPageViewsTopicListMemo);
